import { collectionData, docData } from 'rxfire/firestore';
import { from, throwError, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  query, where, CollectionReference,
} from 'firebase/firestore';
import { CustomError } from '../../utils/error';
import { logger } from '../../utils/logger';

function getFirestoreObservable() {
  return from(import('../../providers')).pipe(
    switchMap(({ FirebaseProvider }) =>
      from(FirebaseProvider.getInstance().getFirestore())
    )
  );
}

export function entityHasItem(entity: string, id: string) {
  return getFirestoreObservable().pipe(
    switchMap((db) => {
      console.log('Entity to be checked:', entity, id);
      return from(getDoc(doc(db, entity, id)));
    }),
    map((snap) => snap.exists())
  );
}

export function getEntityItem<D>(entity: string, id: string) {
  return entityHasItem(entity, id).pipe(
    switchMap((exists) => {
      if (exists) {
        return getFirestoreObservable().pipe(
          switchMap((db) => from(getDoc(doc(db, entity, id)))),
          map((snap) => snap.data() as D)
        );
      } else {
        return throwError(
          new CustomError(
            `Failed to retrieve item from entity: ${entity} - item with id: ${id} was not found`,
            { name: 'NotFoundError', severity: 'ERROR' }
          )
        );
      }
    })
  );
}

export function entitySubCollectionHasItem(
  entity: string,
  parentId: string,
  subCollection: string,
  id: string
) {
  return getFirestoreObservable().pipe(
    switchMap((db) =>
      from(getDoc(doc(db, entity, parentId, subCollection, id)))
    ),
    map((snap) => snap.exists())
  );
}

export function getEntitySubCollectionItem<D>(
  entity: string,
  parentId: string,
  subCollection: string,
  id: string
) {
  return entitySubCollectionHasItem(entity, parentId, subCollection, id).pipe(
    switchMap((exists) => {
      if (exists) {
        return getFirestoreObservable().pipe(
          switchMap((db) =>
            from(getDoc(doc(db, entity, parentId, subCollection, id)))
          ),
          map((snap) => snap.data() as D)
        );
      } else {
        return throwError(
          new CustomError(
            `Failed to retrieve item from parent entity: ${entity}/${parentId} in subCollection: ${subCollection} - Item with id: ${id} was not found`,
            { name: 'NotFoundError', severity: 'ERROR' }
          )
        );
      }
    })
  );
}

export function entityItemByIdObservable<D>(entity: string, id: string) {
  return getFirestoreObservable().pipe(
    switchMap((db) => {
      const docRef = doc(db, entity, id);
      return (docData as any)(docRef, { idField: 'id' }).pipe(
        switchMap((data: D) =>
          from(getDoc(docRef)).pipe(
            map((snap) => (snap.exists() ? data : undefined))
          )
        )
      );
    })
  );
}

export function entityObservable(entity: string) {
  return getFirestoreObservable().pipe(
    switchMap((db) =>
      collectionData(collection(db, entity) as CollectionReference, { idField: 'id' })
    )
  );
}

export function addItemToEntityObservable(
  entity: string,
  data: unknown,
  id: string = undefined
) {
  return getFirestoreObservable().pipe(
    switchMap((db) => from(setDoc(doc(db, entity, id), data as object)))
  );
}

export function updateEntityItemObservable(
  entity: string,
  data: unknown,
  id: string
) {
  logger.info('Called update userProfile: data:', data, 'entity:', entity, ' id:', id);
  return getFirestoreObservable().pipe(
    switchMap((db) =>
      from(updateDoc(doc(db, entity, id), { ...(data as object) }))
    )
  );
}

export function addItemToEntitySubCollection<D>(
  entity: string,
  parentId: string,
  subCollection: string,
  data: D,
  id: string
) {
  return getFirestoreObservable().pipe(
    switchMap((db) =>
      from(setDoc(doc(db, entity, parentId, subCollection, id), data as object))
    )
  );
}

export function subCollectionObservable<D>(
  entity: string,
  parentId: string,
  subCollection: string
) {
  return getFirestoreObservable().pipe(
    switchMap((db) =>
      (collectionData as any)(
        collection(db, entity, parentId, subCollection),
        { idField: 'id' }
      ) as Observable<D[]>
    )
  );
}

export function getSubCollectionObservable<D>(
  entity: string,
  parentId: string,
  subCollection: string
) {
  return getFirestoreObservable().pipe(
    switchMap((db) =>
      from(getDocs(collection(db, entity, parentId, subCollection)))
    ),
    map((snapshot) => snapshot.docs.map((d) => d.data() as unknown as D))
  );
}

export function latestUpdatedSubCollectionObservable<D>(
  entity: string,
  parentId: string,
  subCollection: string,
  time: number
) {
  return getFirestoreObservable().pipe(
    switchMap((db) => {
      const q = query(
        collection(db, entity, parentId, subCollection),
        where('updatedAt', '>', time)
      );
      return (collectionData as any)(q, { idField: 'id' }) as Observable<D[]>;
    }),
    tap((data) => console.log('Updated challenge', data))
  );
}

export function subCollectionItemByIdObservable<D>(
  entity: string,
  parentId: string,
  subCollection: string,
  id: string
): Observable<D> {
  return getFirestoreObservable().pipe(
    switchMap((db) => {
      const docRef = doc(db, entity, parentId, subCollection, id);
      return (docData as any)(docRef, { idField: 'id' }).pipe(
        switchMap((data: D) =>
          from(getDoc(docRef)).pipe(
            map((snap) => (snap.exists() ? data : undefined))
          )
        )
      ) as Observable<D>;
    })
  );
}

export function updateEntitySubCollectionItemObservable<D extends object>(
  entity: string,
  parentId: string,
  subCollection: string,
  data: Partial<D>,
  id: string,
  merge = true
) {
  logger.trace(
    `Called update entity ${entity}: data:`,
    data,
    'subCollection:',
    subCollection,
    ' id:',
    id
  );
  return getFirestoreObservable().pipe(
    switchMap((db) =>
      from(
        setDoc(
          doc(db, entity, parentId, subCollection, id),
          { ...data },
          { merge }
        )
      )
    )
  );
}
